/**
 * OrganiserPage.tsx — 2004va
 *
 * Real event data. Organiser sees:
 * - Event selector (if multiple events)
 * - Live stats: participants, shots, grade dist, pushes, license requests
 * - Active shoot sessions — who is shooting right now
 * - Content list — every shot with grade, score, distribution status
 * - Access code for the event
 * - Filter: all / held / routed / pending requests
 *
 * Realtime: participant joins and new shots update live.
 */

import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Users, Camera, Send, FileCheck, Copy, Check,
         Zap, Lock, Radio } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'

// ── Types ─────────────────────────────────────────────────────────────────────

interface EventRow {
  id:           string
  name:         string
  venue:        string | null
  starts_at:    string | null
  access_code:  string | null
  is_active:    boolean
  sponsor_name: string | null
}

interface EventStats {
  participants:   number
  shots:          number
  held:           number
  routed:         number
  pushes:         number
  actioned:       number
  requests:       number
  grade_a:        number
  grade_b:        number
  grade_c:        number
  grade_d:        number
  avg_score:      number
  active_sessions: number
}

interface ContentRow {
  id:             string
  cloudinary_url: string
  media_type:     string
  file_name:      string | null
  ai_grade:       string | null
  ai_route_score: number | null
  ai_mood:        string | null
  ai_editor_note: string | null
  ai_description: string | null
  status:         string
  created_at:     string
  challenge_status: string | null
  participant_name: string | null
  push_count:     number
  request_count:  number
}

interface SessionRow {
  id:             string
  confirmed_type: string
  session_brief:  string | null
  media_count:    number
  started_at:     string
  participant_name: string | null
}

// ── Data fetching ─────────────────────────────────────────────────────────────

async function fetchOrgEvents(): Promise<EventRow[]> {
  const { data, error } = await supabase
    .from('club_events')
    .select('id, name, venue, starts_at, access_code, is_active, sponsor_name')
    .order('starts_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as EventRow[]
}

async function fetchEventStats(eventId: string): Promise<EventStats> {
  const [mediaRes, participantsRes, sessionsRes] = await Promise.all([
    supabase
      .from('media_items')
      .select('id, status, ai_grade, ai_route_score')
      .eq('event_id', eventId),
    supabase
      .from('participants')
      .select('id')
      .eq('active_event_id', eventId),
    supabase
      .from('shoot_sessions')
      .select('id')
      .eq('event_id', eventId)
      .is('ended_at', null),
  ])

  const media        = mediaRes.data        ?? []
  const participants = participantsRes.data ?? []
  const sessions     = sessionsRes.data     ?? []

  // Push and request counts — separate queries to avoid deep join limits
  const mediaIds = media.map(m => m.id)
  let pushes = 0, actioned = 0, requests = 0

  if (mediaIds.length > 0) {
    const [pushRes, reqRes] = await Promise.all([
      supabase.from('push_queue').select('status').in('media_item_id', mediaIds),
      supabase.from('license_requests').select('status').in('media_item_id', mediaIds),
    ])
    const pushData = pushRes.data ?? []
    pushes   = pushData.length
    actioned = pushData.filter(p => p.status === 'actioned').length
    requests = (reqRes.data ?? []).filter(r => r.status === 'pending').length
  }

  const scores = media.map(m => m.ai_route_score).filter(Boolean) as number[]

  return {
    participants:    participants.length,
    shots:           media.length,
    held:            media.filter(m => m.status === 'held').length,
    routed:          media.filter(m => m.status === 'routed').length,
    pushes,
    actioned,
    requests,
    grade_a:         media.filter(m => m.ai_grade === 'A').length,
    grade_b:         media.filter(m => m.ai_grade === 'B').length,
    grade_c:         media.filter(m => m.ai_grade === 'C').length,
    grade_d:         media.filter(m => m.ai_grade === 'D').length,
    avg_score:       scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0,
    active_sessions: sessions.length,
  }
}

async function fetchEventContent(eventId: string, filter: string): Promise<ContentRow[]> {
  let q = supabase
    .from('media_items')
    .select(`
      id, cloudinary_url, media_type, file_name,
      ai_grade, ai_route_score, ai_mood, ai_editor_note, ai_description,
      status, created_at, challenge_status,
      participant:participants(name)
    `)
    .eq('event_id', eventId)
    .order('created_at', { ascending: false })
    .limit(60)

  if (filter === 'held')     q = q.eq('status', 'held')
  if (filter === 'routed')   q = q.eq('status', 'routed')
  if (filter === 'requests') q = q.not('id', 'is', null) // will filter post-fetch

  const { data, error } = await q
  if (error) throw error

  // Fetch push + request counts per item
  const items = (data ?? []) as unknown as Array<Record<string, unknown> & { participant: { name: string } | null }>
  const ids   = items.map(i => i.id as string)

  let pushCounts:    Record<string, number> = {}
  let requestCounts: Record<string, number> = {}

  if (ids.length > 0) {
    const [pushRes, reqRes] = await Promise.all([
      supabase.from('push_queue').select('media_item_id').in('media_item_id', ids),
      supabase.from('license_requests').select('media_item_id, status').in('media_item_id', ids),
    ])
    ;(pushRes.data ?? []).forEach((p: { media_item_id: string }) => {
      pushCounts[p.media_item_id] = (pushCounts[p.media_item_id] ?? 0) + 1
    })
    ;(reqRes.data ?? []).forEach((r: { media_item_id: string; status: string }) => {
      if (r.status === 'pending') {
        requestCounts[r.media_item_id] = (requestCounts[r.media_item_id] ?? 0) + 1
      }
    })
  }

  const rows: ContentRow[] = items.map(i => ({
    id:               i.id as string,
    cloudinary_url:   i.cloudinary_url as string,
    media_type:       i.media_type as string,
    file_name:        i.file_name as string | null,
    ai_grade:         i.ai_grade as string | null,
    ai_route_score:   i.ai_route_score as number | null,
    ai_mood:          i.ai_mood as string | null,
    ai_editor_note:   i.ai_editor_note as string | null,
    ai_description:   i.ai_description as string | null,
    status:           i.status as string,
    created_at:       i.created_at as string,
    challenge_status: i.challenge_status as string | null,
    participant_name: i.participant?.name ?? null,
    push_count:       pushCounts[i.id as string]    ?? 0,
    request_count:    requestCounts[i.id as string] ?? 0,
  }))

  if (filter === 'requests') return rows.filter(r => r.request_count > 0)
  return rows
}

async function fetchActiveSessions(eventId: string): Promise<SessionRow[]> {
  const { data, error } = await supabase
    .from('shoot_sessions')
    .select(`
      id, confirmed_type, session_brief, media_count, started_at,
      participant:participants(name)
    `)
    .eq('event_id', eventId)
    .is('ended_at', null)
    .order('started_at', { ascending: false })
  if (error) throw error

  return (data as unknown as Array<Record<string, unknown> & { participant: { name: string } | null }>).map(s => ({
    id:               s.id as string,
    confirmed_type:   s.confirmed_type as string,
    session_brief:    s.session_brief as string | null,
    media_count:      s.media_count as number,
    started_at:       s.started_at as string,
    participant_name: s.participant?.name ?? null,
  }))
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const GRADE_STYLE: Record<string, string> = {
  A: 'bg-emerald-900/60 text-emerald-300 border-emerald-700/40',
  B: 'bg-blue-900/60 text-blue-300 border-blue-700/40',
  C: 'bg-amber-900/60 text-amber-300 border-amber-700/40',
  D: 'bg-red-900/60 text-red-300 border-red-700/40',
}

const STATUS_STYLE: Record<string, string> = {
  routed:    'bg-emerald-900/40 text-emerald-400',
  processed: 'bg-blue-900/40 text-blue-400',
  pending:   'bg-neutral-800 text-neutral-400',
  held:      'bg-amber-900/40 text-amber-400',
  archived:  'bg-neutral-900 text-neutral-600',
}

function thumbUrl(url: string) {
  return url.replace('/upload/', '/upload/w_120,h_120,c_fill,q_70,f_auto/')
}

function elapsed(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 60)  return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs}h`
  return `${Math.floor(hrs / 24)}d`
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Stat({ icon, label, value, accent }: {
  icon:    React.ReactNode
  label:   string
  value:   string | number
  accent?: boolean
}) {
  return (
    <div className="bg-room-surface border border-room-border rounded-xl px-3 py-3 space-y-1">
      <div className={cn('flex items-center gap-1.5', accent ? 'text-room-accent' : 'text-room-muted')}>
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-widest">{label}</span>
      </div>
      <p className={cn('text-lg font-black', accent ? 'text-room-accent' : 'text-white')}>{value}</p>
    </div>
  )
}

function GradeBar({ a, b, c, d, total }: { a:number; b:number; c:number; d:number; total:number }) {
  if (total === 0) return <div className="h-2 bg-room-border rounded-full" />
  return (
    <div className="flex h-2 rounded-full overflow-hidden gap-px">
      {a > 0 && <div className="bg-emerald-400" style={{ width: `${(a/total)*100}%` }} title={`A: ${a}`} />}
      {b > 0 && <div className="bg-blue-400"    style={{ width: `${(b/total)*100}%` }} title={`B: ${b}`} />}
      {c > 0 && <div className="bg-amber-400"   style={{ width: `${(c/total)*100}%` }} title={`C: ${c}`} />}
      {d > 0 && <div className="bg-red-400"     style={{ width: `${(d/total)*100}%` }} title={`D: ${d}`} />}
    </div>
  )
}

function AccessCodeBadge({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-2 bg-room-surface border border-room-border rounded-xl px-4 py-2.5 hover:border-room-accent/40 transition-colors"
    >
      <div>
        <p className="text-[10px] text-room-muted font-semibold uppercase tracking-widest">Entry code</p>
        <p className="text-white font-black text-lg tracking-widest">{code}</p>
      </div>
      <span className={cn(
        'ml-auto flex items-center gap-1 text-xs font-semibold transition-colors',
        copied ? 'text-emerald-400' : 'text-room-muted'
      )}>
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? 'Copied' : 'Copy'}
      </span>
    </button>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function OrganiserPage() {
  const qc = useQueryClient()
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'held' | 'routed' | 'requests'>('all')

  const { data: events = [], isLoading: eventsLoading } = useQuery<EventRow[]>({
    queryKey: ['org-events'],
    queryFn:  fetchOrgEvents,
    staleTime: 5 * 60_000,
  })

  const activeEventId = selectedEvent ?? events[0]?.id ?? null
  const activeEvent   = events.find(e => e.id === activeEventId)

  const { data: stats, isLoading: statsLoading } = useQuery<EventStats>({
    queryKey: ['org-stats', activeEventId],
    queryFn:  () => fetchEventStats(activeEventId!),
    enabled:  !!activeEventId,
    refetchInterval: 30_000,
  })

  const { data: content = [], isLoading: contentLoading } = useQuery<ContentRow[]>({
    queryKey: ['org-content', activeEventId, filter],
    queryFn:  () => fetchEventContent(activeEventId!, filter),
    enabled:  !!activeEventId,
    refetchInterval: 30_000,
  })

  const { data: sessions = [] } = useQuery<SessionRow[]>({
    queryKey: ['org-sessions', activeEventId],
    queryFn:  () => fetchActiveSessions(activeEventId!),
    enabled:  !!activeEventId,
    refetchInterval: 20_000,
  })

  // Realtime — new shots and participant joins
  useEffect(() => {
    if (!activeEventId) return
    const channel = supabase
      .channel('org-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'media_items',
          filter: `event_id=eq.${activeEventId}` }, () => {
        qc.invalidateQueries({ queryKey: ['org-stats',   activeEventId] })
        qc.invalidateQueries({ queryKey: ['org-content', activeEventId] })
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'participants',
          filter: `active_event_id=eq.${activeEventId}` }, () => {
        qc.invalidateQueries({ queryKey: ['org-stats', activeEventId] })
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'shoot_sessions',
          filter: `event_id=eq.${activeEventId}` }, () => {
        qc.invalidateQueries({ queryKey: ['org-sessions', activeEventId] })
        qc.invalidateQueries({ queryKey: ['org-stats',    activeEventId] })
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [activeEventId, qc])

  if (eventsLoading) return (
    <div className="flex justify-center py-16"><Spinner size={28} /></div>
  )

  if (events.length === 0) return (
    <div className="text-center py-16 space-y-2">
      <p className="text-room-muted text-sm">No events found</p>
      <p className="text-room-muted/60 text-xs">Events are created in Supabase — contact your admin</p>
    </div>
  )

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── Event selector ── */}
      {events.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {events.map(ev => (
            <button
              key={ev.id}
              type="button"
              onClick={() => { setSelectedEvent(ev.id); setFilter('all') }}
              className={cn(
                'shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap',
                activeEventId === ev.id
                  ? 'bg-room-accent text-white'
                  : 'bg-room-surface border border-room-border text-room-muted hover:text-white'
              )}
            >
              {ev.name}
              {ev.is_active && (
                <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse-dot" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── Event header ── */}
      {activeEvent && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-white font-bold text-lg">{activeEvent.name}</h2>
              {activeEvent.is_active && (
                <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400 bg-emerald-900/30 border border-emerald-700/30 px-2 py-0.5 rounded-full">
                  <Radio size={9} className="animate-pulse" />
                  Live
                </span>
              )}
            </div>
            {activeEvent.venue && (
              <p className="text-room-muted text-xs mt-0.5">{activeEvent.venue}</p>
            )}
            {activeEvent.sponsor_name && (
              <p className="text-room-muted/60 text-[10px] mt-0.5">Sponsored by {activeEvent.sponsor_name}</p>
            )}
          </div>
          {activeEvent.access_code && (
            <AccessCodeBadge code={activeEvent.access_code} />
          )}
        </div>
      )}

      {/* ── Stats grid ── */}
      {statsLoading ? (
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-room-surface animate-pulse" />
          ))}
        </div>
      ) : stats && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat icon={<Users size={13} />}    label="In The Room"  value={stats.participants} accent />
            <Stat icon={<Camera size={13} />}   label="Shots"        value={stats.shots} />
            <Stat icon={<Radio size={13} />}    label="Shooting now" value={stats.active_sessions} />
            <Stat icon={<Send size={13} />}     label="Distributed"  value={stats.pushes} />
            <Stat icon={<FileCheck size={13} />} label="Requests"    value={stats.requests}
              accent={stats.requests > 0} />
            <Stat icon={<Zap size={13} />}      label="Avg score"
              value={stats.shots > 0 ? `${Math.round(stats.avg_score * 100)}%` : '—'} />
          </div>

          {/* Grade bar */}
          {stats.shots > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-room-muted font-semibold uppercase tracking-widest">Grade distribution</span>
                <div className="flex items-center gap-2 text-[10px] text-room-muted">
                  <span className="text-emerald-400">A:{stats.grade_a}</span>
                  <span className="text-blue-400">B:{stats.grade_b}</span>
                  <span className="text-amber-400">C:{stats.grade_c}</span>
                  <span className="text-red-400">D:{stats.grade_d}</span>
                </div>
              </div>
              <GradeBar a={stats.grade_a} b={stats.grade_b} c={stats.grade_c} d={stats.grade_d} total={stats.shots} />
            </div>
          )}

          {/* Held notice */}
          {stats.held > 0 && (
            <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2.5">
              <Lock size={13} className="text-amber-300 flex-shrink-0" />
              <p className="text-xs text-amber-300">
                <span className="font-semibold">{stats.held} shot{stats.held > 1 ? 's' : ''} held</span>
                {' '}— anonymous uploads awaiting identity capture
              </p>
            </div>
          )}

          {/* Requests notice */}
          {stats.requests > 0 && (
            <div className="flex items-center gap-3 bg-room-accent/10 border border-room-accent/20 rounded-xl px-4 py-2.5">
              <FileCheck size={13} className="text-room-accent flex-shrink-0" />
              <p className="text-xs text-room-accent">
                <span className="font-semibold">{stats.requests} license request{stats.requests > 1 ? 's' : ''}</span>
                {' '}pending creator approval
              </p>
            </div>
          )}
        </>
      )}

      {/* ── Active sessions ── */}
      {sessions.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-room-muted mb-2">
            Shooting now — {sessions.length} active
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {sessions.map(s => (
              <div key={s.id}
                className="shrink-0 bg-room-surface border border-room-border rounded-xl px-3 py-2 min-w-[140px]">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                  <span className="text-white text-xs font-semibold truncate">
                    {s.participant_name ?? 'Anonymous'}
                  </span>
                </div>
                <p className="text-room-muted text-[10px] truncate capitalize">
                  {s.confirmed_type?.replace('_', ' ') ?? 'General'}
                </p>
                {s.session_brief && (
                  <p className="text-room-muted/60 text-[10px] truncate mt-0.5">{s.session_brief}</p>
                )}
                <p className="text-room-muted text-[10px] mt-1">
                  {s.media_count} shot{s.media_count !== 1 ? 's' : ''} · {elapsed(s.started_at)} ago
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Content filter ── */}
      <div className="flex gap-1.5">
        {(['all', 'routed', 'held', 'requests'] as const).map(f => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-semibold transition-all capitalize',
              filter === f
                ? 'bg-room-accent text-white'
                : 'bg-room-surface border border-room-border text-room-muted hover:text-white'
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {/* ── Content list ── */}
      {contentLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-room-surface animate-pulse" />
          ))}
        </div>
      ) : content.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-room-muted text-sm">No shots match this filter</p>
        </div>
      ) : (
        <div className="space-y-2">
          {content.map(item => (
            <div key={item.id}
              className="bg-room-surface border border-room-border rounded-xl p-3 flex gap-3">

              {/* Thumbnail */}
              {item.media_type === 'photo' ? (
                <img
                  src={thumbUrl(item.cloudinary_url)}
                  alt=""
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-room-border flex items-center justify-center flex-shrink-0">
                  <span className="text-room-muted text-xs capitalize">{item.media_type}</span>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0 space-y-1.5">
                {/* Top row — grade + score + status */}
                <div className="flex items-center gap-2">
                  {item.ai_grade && (
                    <span className={cn(
                      'text-[10px] font-black px-1.5 py-0.5 rounded border flex-shrink-0',
                      GRADE_STYLE[item.ai_grade] ?? GRADE_STYLE.D
                    )}>
                      {item.ai_grade}
                    </span>
                  )}
                  {item.ai_route_score != null && (
                    <span className="flex items-center gap-0.5 text-room-gold text-[10px] font-semibold">
                      <Zap size={9} />
                      {Math.round(item.ai_route_score * 100)}%
                    </span>
                  )}
                  {item.ai_mood && (
                    <span className="text-room-muted text-[10px] capitalize">{item.ai_mood}</span>
                  )}
                  <span className={cn(
                    'ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded capitalize flex-shrink-0',
                    STATUS_STYLE[item.status] ?? 'bg-room-border text-room-muted'
                  )}>
                    {item.status}
                  </span>
                </div>

                {/* Description / note */}
                <p className="text-white text-xs truncate">
                  {item.ai_description ?? item.file_name ?? '—'}
                </p>

                {/* Creator + distribution */}
                <div className="flex items-center gap-3 text-[10px] text-room-muted">
                  {item.participant_name && (
                    <span>{item.participant_name}</span>
                  )}
                  {item.push_count > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Send size={9} />
                      {item.push_count}
                    </span>
                  )}
                  {item.request_count > 0 && (
                    <span className="flex items-center gap-0.5 text-room-accent font-semibold">
                      <FileCheck size={9} />
                      {item.request_count} request{item.request_count > 1 ? 's' : ''}
                    </span>
                  )}
                  {item.challenge_status === 'pending' && (
                    <span className="text-purple-400">Under review</span>
                  )}
                  <span className="ml-auto">{elapsed(item.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
